import { useQuery } from '@apollo/client/react'
import { createContext, useContext, useEffect, useMemo } from 'react'
import { GQL_APP_CONFIG } from '~/gql/app-config/app-config'

export type Branding = {
  appName: string
  logo?: string | null
  favicon?: string | null
  primaryColor?: string | null
  accentColor?: string | null
  headingFont?: string | null
  bodyFont?: string | null
  footerText?: string | null
  defaultLocale?: string | null
  /** Plan gratuit : white-label verrouillé, pied de page « propulsé par » imposé (#155). */
  brandingLocked?: boolean | null
  /**
   * L'écran de connexion porte l'accroche d'acquisition de la plateforme (#216).
   * Vrai sur le gratuit seulement — sur une org cliente, ses apprenants ne voient
   * rien de nous à la connexion.
   */
  signupCtaVisible?: boolean | null
  /**
   * Fuseau de l'organisation (IANA). Découpe les jours des bornes de parcours :
   * toute date de session s'affiche dedans, pas dans celui du navigateur (#194).
   */
  timezone?: string | null
}

const DEFAULT_BRANDING: Branding = {
  appName: 'LMS',
  brandingLocked: false,
  // Masquée tant que la query n'a pas répondu : le mauvais défaut ferait
  // clignoter l'accroche d'acquisition sur l'écran de connexion d'un client.
  signupCtaVisible: false,
  // Miroir du `DEFAULT_TIMEZONE` de l'API, le temps que la query réponde.
  timezone: 'Europe/Paris',
}

const BrandingContext = createContext<Branding>(DEFAULT_BRANDING)

/** Applique une variable CSS sur <html> ou la retire si la valeur est vide. */
function applyCssVar(name: string, value?: string | null) {
  const root = document.documentElement
  if (value) root.style.setProperty(name, value)
  else root.style.removeProperty(name)
}

/** Met à jour le <link rel="icon"> (crée le tag s'il n'existe pas). */
function applyFavicon(href?: string | null) {
  if (!href) return
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.href = href
}

/**
 * Récupère la configuration white-label (query publique, dispo avant login) et
 * l'applique au runtime : titre de l'onglet, favicon, tokens de couleur/typo.
 * Expose le branding aux composants via useBranding().
 */
export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { data } = useQuery(GQL_APP_CONFIG)

  const branding = useMemo<Branding>(
    () => ({ ...DEFAULT_BRANDING, ...(data?.appConfig ?? {}) }),
    [data],
  )

  useEffect(() => {
    document.title = branding.appName
    applyFavicon(branding.favicon)
    applyCssVar('--color-primary', branding.primaryColor)
    applyCssVar('--color-accent', branding.accentColor)
    applyCssVar('--font-heading', branding.headingFont)
    applyCssVar('--font-body', branding.bodyFont)
  }, [branding])

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  )
}

export function useBranding(): Branding {
  return useContext(BrandingContext)
}
